import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TenantContext = createContext(null);

/**
 * Resolve which tenant slug to load, in priority order:
 *   1. `?tenant=slug` query param (dev/preview override — highest priority)
 *   2. Backend host→slug lookup (?host=xxx) — set at tenant config
 *   3. URL path segment /:tenantSlug (fallback for preview/legacy)
 *   4. Default to "mymaitree"
 * Backend endpoint `/api/tenant/config` accepts `slug` or `host` query params.
 */
function _resolveHint() {
  const params = new URLSearchParams(window.location.search);
  const override = params.get("tenant");
  if (override) return { slug: override, host: null };
  // If path first segment is present and not a global route, use it as slug hint
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const first = pathParts[0];
  const slug = first && first !== "admin" && first !== "maitree" ? first : null;
  return { slug, host: window.location.hostname };
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { slug, host } = _resolveHint();
        let url = `${API}/tenant/config`;
        const qs = new URLSearchParams();
        if (slug) qs.set("slug", slug);
        if (host) qs.set("host", host);
        if ([...qs].length) url += `?${qs.toString()}`;
        const res = await axios.get(url);
        setTenant(res.data);
      } catch {
        setTenant({
          name: "My Maitree", slug: "mymaitree",
          modules: ["core", "guest_portal", "memberships", "events", "event_commerce", "payments", "analytics"],
          branding: { primary_color: "#1e3a5f", accent_color: "#2d8a4e" },
          logo_url: "/mymaitree-logo.png", tagline: "",
        });
      } finally { setLoading(false); }
    };
    fetchConfig();
  }, []);

  const hasModule = (moduleId) => {
    if (!tenant) return true; // default allow during load
    return tenant.modules?.includes(moduleId);
  };

  const getTenantSlug = () => {
    return tenant?.slug || "mymaitree";
  };

  const refreshConfig = async () => {
    try {
      const slug = tenant?.slug;
      const url = slug ? `${API}/tenant/config?slug=${slug}` : `${API}/tenant/config`;
      const res = await axios.get(url);
      setTenant(res.data);
    } catch {}
  };

  return (
    <TenantContext.Provider value={{ tenant, loading, hasModule, getTenantSlug, refreshConfig }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}

export { TenantContext };
